import { CompartmentSelector } from '@ekkogmbh/apisdk';
import { Divider, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';

interface Props {
  configuration: CompartmentSelector['configuration'];
}

const ConfigEntry = ({ label, value }: { label: string; value: ReactNode }): React.JSX.Element => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Stack>
);

const ConfigSection = ({ title, children }: { title: string; children: ReactNode }): React.JSX.Element => (
  <Stack spacing={0.5}>
    <Typography variant="caption" fontWeight={700} textTransform="uppercase" color="text.secondary" letterSpacing={0.5}>
      {title}
    </Typography>
    {children}
  </Stack>
);

export default function CompartmentSelectorConfigurationField({ configuration }: Props): React.JSX.Element {
  const sections = useMemo(() => {
    // @TODO specify this type in apisdk via extend
    const config = configuration as Record<string, string | string[] | Record<string, string>>;
    const elements: React.JSX.Element[] = [];

    for (const [key, value] of Object.entries(config)) {
      if (value === undefined || value === null) {
        continue;
      }

      if (key === 'fields') {
        elements.push(<ConfigEntry key={key} label="fields" value={(value as string[]).join(', ')} />);
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        // Nested object (e.g. regex: { pattern, replacement })
        const nested = value as Record<string, string>;
        elements.push(
          <ConfigSection key={key} title={key}>
            {Object.entries(nested).map(([subKey, subValue]) => (
              <ConfigEntry key={subKey} label={subKey} value={subValue} />
            ))}
          </ConfigSection>,
        );
      } else {
        elements.push(<ConfigEntry key={key} label={key} value={String(value)} />);
      }
    }

    return elements;
  }, [configuration]);

  return (
    <Stack spacing={1} divider={<Divider />}>
      {sections}
    </Stack>
  );
}
